<template>
    <div class="content-wrapper p-0">

        <!-- Fallback (no hero config) -->
        <div v-if="!checkCondition(careerPage)" class="hero position-relative pt-5">
            <div class="hero__item--content container">
                <div class="d-flex justify-content-end mb-3">
                    <a
                        :href="urlGenerator(loggedIn ? 'admin/dashboard' : 'admin/users/login')"
                        class="btn btn-primary"
                    >
                        {{ $t(loggedIn ? 'dashboard' : 'login') }}
                    </a>
                </div>
            </div>
        </div>

        <home-header
            v-if="checkCondition(careerPage)"
            :logo="logo"
            :loggedIn="loggedIn"
            :bodySection="careerPage.job_post_settings.content.bodySection"
            @open-modal="$refs.hero && $refs.hero.toggleModal($event)"
        />

        <hero
            v-if="checkCondition(careerPage)"
            ref="hero"
            :is-candidate="isCandidate"
            :cover="jobPostCover"
            :icon="icon"
            :logged-in="loggedIn"
            :logo="logo"
            :pageSettingContent="careerPage.job_post_settings.content.bodySection"
            :body-section="careerPage.job_post_settings.content.bodySection"
            :active-preview="activePreview"
        >
            <!-- Hero slot content -->
            <template #content>
                <div v-if="pageBlocks[pageView].header" class="--text-center mb-5">
                    <h1
                        :style="`font-size: ${titleStyle.fontSize};
                                 font-weight: ${titleStyle.fontWeight};
                                 letter-spacing: ${titleStyle.letterSpacing};
                                 color: ${titleStyle.color};`"
                        class="mb-4"
                    >
                        {{ content.title }}
                    </h1>

                    <p
                        :style="`font-size: ${subTitleStyle.fontSize};
                                 font-weight: ${subTitleStyle.fontWeight};
                                 letter-spacing: ${subTitleStyle.letterSpacing};
                                 color: ${subTitleStyle.color};`"
                        class="mb-4"
                    >
                        {{ content.subtitle }}
                    </p>
                </div>
            </template>
        </hero>

        <!-- PAGE CONTENT -->
        <div class="editor-content">
            <div class="preview-content career-page" style="padding-top: 0 !important;">
                <div class="preview container">

                    <!-- Header fallback when hero disabled -->
                    <template
                        v-if="!(careerPage &&
                                careerPage.job_post_settings &&
                                careerPage.job_post_settings.content &&
                                careerPage.job_post_settings.content.hero)"
                    >
                        <div
                            v-if="pageBlocks[pageView].logo"
                            class="d-flex flex-column align-items-center mb-5"
                        >
                            <img
                                :src="icon"
                                alt=""
                                class="candidate-viewable-icon img-fluid"
                            />
                        </div>

                        <div v-if="pageBlocks[pageView].header" class="text-center mb-5">
                            <h1 :style="`font-size: ${titleStyle.fontSize}; font-weight: ${titleStyle.fontWeight}; color: ${titleStyle.color};`">
                                {{ content.title }}
                            </h1>
                            <p :style="`font-size: ${subTitleStyle.fontSize}; font-weight: ${subTitleStyle.fontWeight}; color: ${subTitleStyle.color};`">
                                {{ content.subtitle }}
                            </p>
                        </div>
                    </template>

                    <!-- Search -->
                    <div class="d-flex justify-content-center">
                        <Search
                            class="w-75 w-md-50"
                            :readonly="false"
                            @enterpress="handleEnterPress"
                            @search-term-clear="fetchJobs"
                        />
                    </div>

                    <!-- Location Filter-->
                    <div class="location-filter-wrapper mt-4">

                        <div class="location-card-container">

                            <div
                                class="location-card-header bg-primary text-white"
                                @click="showLocations = !showLocations"
                            >
                                <span>
                                    {{ showLocations 
                                        ? 'Hide Locations' 
                                        : `Search by Location${selectedLocations.length ? ' (' + selectedLocations.length + ')' : ''}` 
                                    }}
                                </span>

                                <div class="d-flex align-items-center mr-2">

                                    <button
                                        v-if="selectedLocations.length"
                                        class="clear-btn mr-2"
                                        @click.stop="clearLocations"
                                    >
                                        Clear
                                    </button>

                                    <span class="arrow text-white" :class="{ open: showLocations }">
                                        <i class="fas fa-chevron-down"></i>
                                    </span>

                                </div>
                            </div>

                            <div v-if="selectedLocations.length && !showLocations" class="selected-preview">
                                <div class="location-chips">

                                    <div
                                        v-for="location in locations.filter(l => selectedLocations.includes(l.id))"
                                        :key="'selected-' + location.id"
                                        class="chip active"
                                        @click="toggleLocation(location.id)"
                                    >
                                        {{ location.address }}
                                    </div>

                                </div>
                            </div>

                            <transition name="collapse">
                                <div v-if="showLocations" class="location-card-body">

                                    <div class="location-chips">

                                        <div
                                            v-for="location in locations"
                                            :key="location.id"
                                            class="chip"
                                            :class="{ active: selectedLocations.includes(location.id) }"
                                            @click="toggleLocation(location.id)"
                                        >
                                            {{ location.address }}
                                        </div>

                                    </div>

                                </div>
                            </transition>

                        </div>

                    </div>

                    <!-- Site Name Filter-->
                    <div class="location-filter-wrapper mt-4">

                        <div class="location-card-container">

                            <div
                                class="location-card-header bg-primary text-white"
                                @click="showSiteNames = !showSiteNames"
                            >
                                <span>
                                    {{ showSiteNames
                                        ? 'Hide Site Names'
                                        : `Search by Site Name${selectedSiteNames.length ? ' (' + selectedSiteNames.length + ')' : ''}`
                                    }}
                                </span>

                                <div class="d-flex align-items-center mr-2">

                                    <button
                                        v-if="selectedSiteNames.length"
                                        class="clear-btn mr-2"
                                        @click.stop="clearSiteNames"
                                    >
                                        Clear
                                    </button>

                                    <span class="arrow text-white" :class="{ open: showSiteNames }">
                                        <i class="fas fa-chevron-down"></i>
                                    </span>

                                </div>
                            </div>

                            <div
                                v-if="selectedSiteNames.length && !showSiteNames"
                                class="selected-preview"
                            >
                                <div class="location-chips">

                                    <div
                                        v-for="site in availableSiteNames.filter(
                                            s => selectedSiteNames.includes(s.id)
                                        )"
                                        :key="'selected-site-' + site.id"
                                        class="chip active"
                                        @click="toggleSiteName(site.id)"
                                    >
                                        {{ site.sname }}
                                    </div>

                                </div>
                            </div>

                            <transition name="collapse">

                                <div v-if="showSiteNames" class="location-card-body">

                                    <div class="location-chips">

                                        <div
                                            v-for="site in availableSiteNames"
                                            :key="site.id"
                                            class="chip"
                                            :class="{ active: selectedSiteNames.includes(site.id) }"
                                            @click="toggleSiteName(site.id)"
                                        >
                                            {{ site.sname }}
                                        </div>

                                    </div>

                                </div>

                            </transition>

                        </div>

                    </div>

                    <!-- Jobs -->
                    <div id="jobs" class="mt-5">
                        <h5
                            :style="`font-size: ${headingsStyle.fontSize};
                                     font-weight: ${headingsStyle.fontWeight};
                                     color: ${headingsStyle.color};`"
                            class="job-section-heading"
                        >
                            <template v-if="!jobsLoading">
                                {{ $t('latest_jobs') }}
                                <span class="text-muted">
                                    ({{ totalJobs }})
                                </span>
                            </template>
                        </h5>

                        <div class="job-openings">
                            <app-pre-loader v-if="jobsLoading" />
                            <div class="row" v-else>
                                <div
                                    v-for="job in jobList"
                                    :key="job.id"
                                    :class="{ 'col-md-6 col-xl-4': activePreview === 'desktop' }"
                                    class="col-12 mb-primary"
                                >
                                    <JobOpeningCard :job="job" />
                                </div>
                                <div class="col-12" v-if="newJobsLoading">
                                    <app-pre-loader />
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Footer -->
                    <div v-if="pageBlocks[pageView].footer" class="text-center py-4">
                        {{ companyText }}
                    </div>

                </div>
            </div>
        </div>

    </div>
</template>

<script>
import { axiosGet, urlGenerator } from "../../../Helpers/AxiosHelper";
import { PUBLIC_JOB_POST, JOB_LIST } from "../../../Config/ApiUrl";
import Hero from "../../Helpers/Editor/Hero";
import JobOpeningCard from "../../Helpers/Editor/JobOpeningCard";
import Search from "../../Helpers/Editor/Search";
import { textTruncate } from "../../../Helpers/TextHelper";
import HeroDropdown from "./HeroDropdown.vue";
import { TENANT_BASE_URL } from "../../../Config/UrlHelper";
import HomeHeader from "../HomeHeader/HomeHeader.vue";

export default {
    name: 'CandidateCareerPage',
    components: { Hero, JobOpeningCard, Search, HeroDropdown, HomeHeader },
    props: {
        careerPage: {},
        companyLocations: {},
        siteNames: {},
        // jobPosts: {},
        loggedIn: {
            type: Boolean,
            default: false
        },
        isCandidate: {
            type: Boolean,
            required: true
        }
    },
    filters: {
        truncate(value) {
            const limit = 40;
            if (value.length < limit) return value;
            return value.substring(0, limit) + '...'
        }
    },
    data() {
        return {
            urlGenerator,
            searchTerm: '',
            textTruncate,
            viewType: 'desktop',
            activePreview: 'desktop',
            showAboutUsModal: false,
            year: moment(moment.now()).format("YYYY"),
            jobsLoading: false,
            jobPosts: [],
            nextPageUrl: '',
            totalJobs: null,
            newJobsLoading: false,
            selectedLocations: [],
            showLocations: false,
            selectedSiteNames: [],
            showSiteNames: false,
        }
    },
    computed: {
        jobList() {
            return this.jobPosts.map(item => {
                return {
                    ...item,
                    title: item.name,
                    type: item['job_type'].name,
                    location: item.locations?.map(location => location.address).join(', ') || '',
                    site_names: item.site_names?.map(site => site.sname).join(', ') || '',
                    department: item['department'] ? item['department'].name : '',
                    url: urlGenerator(`${TENANT_BASE_URL}/${item.slug}`),
                }
            })
            // .filter(item =>
            //     item.title.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
            //     item.type.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
            //     item.salary.toString().toLowerCase().includes(this.searchTerm.toLowerCase()) ||
            //     item.location.toLowerCase().includes(this.searchTerm.toLowerCase())
            // );
        },
        careerPageData() {
            let data = (typeof this.careerPage === 'string') ? JSON.parse(this.careerPage) : this.careerPage;
            return data.job_post_settings ? data.job_post_settings : data;
        },
        content() {
            return this.careerPageData.content;
        },
        pageStyle() {
            return this.careerPageData.pageStyle;
        },
        pageBlocks() {
            if (this.careerPageData.pageBlocks) return this.careerPageData.pageBlocks;
            return null;
        },
        pageView() {
            return this.activePreview === 'desktop' ? 'defaultView' : 'mobileView'
        },
        titleStyle() {
            return this.pageStyle[this.pageView].find(item => item.key === 'title')
        },
        subTitleStyle() {
            return this.pageStyle[this.pageView].find(item => item.key === 'sub-title')
        },
        detailsStyle() {
            return this.pageStyle[this.pageView].find(item => item.key === 'details')
        },
        headingsStyle() {
            return this.pageStyle[this.pageView].find(item => item.key === 'headings')
        },
        descriptionStyle() {
            return this.pageStyle[this.pageView].find(item => item.key === 'description')
        },
        icon() {
            return this.careerPageData.icon ? this.careerPageData.icon : urlGenerator(window.settings.company_icon)
        },
        logo() {
            return urlGenerator(window.settings.company_logo);
        },
        jobPostCover() {
            return urlGenerator(window.settings.job_post_cover);
        },
        companyText() {
            return `${this.$t('copyright_text') + ' ' + this.year + ' ' + this.$t('by') + ' '} ${window.settings?.company_name ? window.settings.company_name : ''}`;
        },
        locations() {
            return this.companyLocations || []
        },
        availableSiteNames() {
            return this.siteNames || []
        }
    },
    watch: {
        viewType: {
            handler: function (type) {
                this.toggleResponsivePreview(type)
            },
            immediate: true
        }
    },
    mounted() {
        this.checkViewType();
        window.onresize = () => {
            this.checkViewType();
        }
        this.fetchJobs()
        document.body.addEventListener('scroll', this.handleScroll)
    },
    unmounted() {
        document.body.removeEventListener('scroll', this.handleScroll)
    },
    methods: {
        fetchJobs() {

            this.jobsLoading = true

            let url = JOB_LIST + '?per_page=10'

            if (this.selectedLocations.length) {
                url += '&location_id=' + this.selectedLocations.join(',')
            }

            if (this.selectedSiteNames.length) {
                url += '&site_name_id=' + this.selectedSiteNames.join(',')
            }

            axiosGet(url)
                .then(response => {

                    this.jobPosts = response?.data?.data
                    this.nextPageUrl = response.data.next_page_url
                    this.totalJobs = response?.data?.total

                })
                .catch(e => console.dir(e))
                .finally(() => this.jobsLoading = false)

        },
        searchJobs(searchTerm) {
            this.jobsLoading = true
            axiosGet(JOB_LIST + '?search=' + searchTerm)
                .then(response => {
                    this.jobPosts = response?.data?.data 
                    this.totalJobs = response?.data?.total
                })
                .catch(e => console.dir(e))
                .finally(() => this.jobsLoading = false)
        },
        fetchNewJobs() {

            this.newJobsLoading = true

            let url = this.nextPageUrl + '&per_page=10'

            if (this.selectedLocations.length) {
                url += '&location_id=' + this.selectedLocations.join(',')
            }

            axiosGet(url)
                .then(response => {

                    this.jobPosts = [...this.jobPosts, ...response?.data?.data]
                    this.nextPageUrl = response?.data?.next_page_url

                })
                .catch(e => console.dir(e))
                .finally(() => this.newJobsLoading = false)

        },
        toggleModal() {
            this.$refs.hero.toggleModal();
        },
        handleReadMoreBtnClick() {
            this.showAboutUsModal = true;
        },
        checkCondition(careerPage) {
            return careerPage?.job_post_settings?.content?.hero;
        },
        checkViewType() {
            this.viewType = window.innerWidth > 575 ? 'desktop' : 'mobile';
        },
        toggleResponsivePreview(previewType) {
            let preview = $('.preview-content .preview')

            if (previewType === 'desktop') {
                this.activePreview = 'desktop';
                preview.removeClass('mobile-preview');
                preview.addClass('desktop-preview');
            } else {
                this.activePreview = 'mobile';
                preview.removeClass('desktop-preview');
                preview.addClass('mobile-preview');
            }
        },
        handleScroll(e) {
            const box = e.target;
            const scrollableHeight = box.scrollHeight - box.clientHeight

            if (box.scrollTop >= scrollableHeight) return this.handleScrollToBottom()
        },
        handleEnterPress(searchTerm) {
            if (!searchTerm) return this.fetchJobs()
            this.searchJobs(searchTerm);
        },
        handleScrollToBottom() {
            if (!this.nextPageUrl) return;
            this.fetchNewJobs();
        },
        toggleLocation(locationId) {

            if (this.selectedLocations.includes(locationId)) {

                this.selectedLocations =
                    this.selectedLocations.filter(id => id !== locationId)

            } else {

                this.selectedLocations.push(locationId)

            }

            this.fetchJobs()

        },
        toggleSiteName(siteId) {

            if (this.selectedSiteNames.includes(siteId)) {

                this.selectedSiteNames =
                    this.selectedSiteNames.filter(id => id !== siteId)

            } else {

                this.selectedSiteNames.push(siteId)

            }

            this.fetchJobs()

        },
        clearLocations() {
            this.selectedLocations.splice(0)
            this.fetchJobs()
        },
        clearSiteNames() {
            this.selectedSiteNames.splice(0)
            this.fetchJobs()
        },
    }
}
</script>

<style scoped lang="scss">
.hero {
    border-top: 1px solid rgba(0, 0, 0, 0.08);    
    padding-bottom: 5rem;
}

.font-20 {
    font-size: 20px;
}

.editor-content {
    margin-top: -4rem;
}

.location-filter-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
}

.location-card-container {
  width: 100%;
  max-width: 720px;
  background: var(--default-card-bg);
  border-radius: 16px;
  box-shadow: var(--default-box-shadow);
  border: 1px solid var(--default-border-color);
  overflow: hidden;
}

.location-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  cursor: pointer;
  font-weight: 500;
  font-size: 15px;
  transition: 0.2s ease;
}

.location-card-header:hover {
  opacity: 0.95;
}

.arrow {
  font-size: 20px;
  transition: transform 0.3s ease;
  display: inline-flex;
  align-items: center;
}

.arrow.open {
  transform: rotate(180deg);
}

.location-card-body {
  padding: 15px;
  background-color: var(--default-card-bg);
}

.location-chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
}

.chip {
  min-width: 120px;
  max-width: 160px;
  padding: 8px 12px;
  border-radius: 999px;
  font-size: 13px;
  cursor: pointer;
  background-color: #e5e7eb;
  color: #374151;
  border: 1px solid #e5e7eb;
  transition: 0.2s;

  display: flex;
  align-items: center;
  justify-content: center;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chip:hover {
  background-color: #d1d5db;
}

.chip.active {
  background-color: #dc2626;
  color: #fff;
  border-color: #dc2626;
}

.collapse-enter-active,
.collapse-leave-active {
  transition: all 0.25s ease;
}

.collapse-enter-from,
.collapse-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.collapse-enter-to,
.collapse-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.selected-preview {
  padding: 10px 15px;
  border-top: 1px solid var(--default-border-color);
}

.clear-btn {
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid #ddd;
  background: #fff;
  color: #000;
  cursor: pointer;
  transition: all 0.2s ease;
}

.clear-btn:hover {
  background: #eef2ff;
}
</style>
